<script setup lang="ts">
import { useRoute } from 'vue-router'
import { ArrowTopRightOnSquareIcon, ChevronDownIcon } from '@heroicons/vue/24/outline'
import { HUB_ACCENTS } from '@/v2/data/sidebarMenu'
import type { HubAccent, SidebarMenuEntry } from '@/v2/data/sidebarMenu'
import { useSidebarStore } from '@/v2/stores/useSidebarStore'

const props = withDefaults(
  defineProps<{
    entries: SidebarMenuEntry[]
    /** Mode replié (desktop) : icônes seules, un clic sur hub redéplie la sidebar */
    collapsed?: boolean
    /** Préfixe des ids ARIA — la liste est rendue 2× (aside desktop + dialog mobile) */
    idPrefix: string
  }>(),
  { collapsed: false },
)

const route = useRoute()
const sidebarStore = useSidebarStore()

/** Couleur d'accent d'un hub (jeton de marque) ; primary par défaut. */
function accentColor(key?: HubAccent): string {
  return key ? HUB_ACCENTS[key].color : 'var(--color-primary)'
}
/** Teinte de l'icône de section (toujours visible). */
function accentStyle(entry: SidebarMenuEntry) {
  return entry.accent ? { color: HUB_ACCENTS[entry.accent].color } : undefined
}
/** Item actif : fond teinté + texte assombri (lisible, AA) dans la couleur de section. */
function activeLinkStyle(active: boolean, accentKey?: HubAccent) {
  if (!active) return undefined
  const c = accentColor(accentKey)
  return {
    color: `color-mix(in oklab, ${c} 60%, var(--color-base-content))`,
    backgroundColor: `color-mix(in oklab, ${c} 12%, transparent)`,
  }
}
/** Hub fermé contenant la route active : libellé dans la couleur de section. */
function hubHighlightStyle(entry: SidebarMenuEntry) {
  if (!(containsActive(entry) && !isExpanded(entry))) return undefined
  return { color: `color-mix(in oklab, ${accentColor(entry.accent)} 60%, var(--color-base-content))` }
}
/** Ligne verticale du sous-menu, teintée avec la couleur de section (bien visible). */
function submenuBorderStyle(accentKey?: HubAccent) {
  return { borderColor: `color-mix(in oklab, ${accentColor(accentKey)} 70%, var(--color-base-300))` }
}

function isActive(to?: string): boolean {
  if (!to) return false
  return route.path === to || route.path.startsWith(to + '/')
}

/** Un hub est "actif" si un de ses descendants l'est (repère quand la section est fermée). */
function containsActive(entry: SidebarMenuEntry): boolean {
  if (isActive(entry.to)) return true
  return entry.children?.some(containsActive) ?? false
}

function isExpanded(entry: SidebarMenuEntry): boolean {
  return !props.collapsed && sidebarStore.isSectionExpanded(entry.id)
}

function onHubClick(entry: SidebarMenuEntry) {
  if (props.collapsed) {
    // Spec MASTER §10 : en mode replié, déplier d'abord la sidebar PUIS ouvrir la section
    sidebarStore.setCollapsed(false)
    sidebarStore.expandSection(entry.id)
  } else {
    sidebarStore.toggleSection(entry.id)
  }
}

const rowBase =
  'flex w-full items-center gap-2 min-h-11 rounded-field px-2 text-sm font-medium ' +
  'transition-colors motion-reduce:transition-none focus-visible:outline-2 focus-visible:outline-primary'

function linkClasses(entry: SidebarMenuEntry): string {
  // Couleur active gérée par activeLinkStyle (couleur de section, inline)
  return isActive(entry.to)
    ? `${rowBase} font-semibold`
    : `${rowBase} text-base-content/80 hover:bg-base-200 hover:text-base-content`
}

function hubClasses(): string {
  return `${rowBase} text-base-content/80 hover:bg-base-200 hover:text-base-content`
}
</script>

<template>
  <ul role="list" class="flex flex-col gap-1">
    <li v-for="entry in entries" :key="entry.id">
      <!-- Hub accordéon (niveau 1 avec enfants) -->
      <template v-if="entry.children">
        <button
          type="button"
          :class="[hubClasses(), collapsed ? 'justify-center px-0' : '']"
          :style="hubHighlightStyle(entry)"
          :aria-expanded="isExpanded(entry)"
          :aria-controls="`submenu-${idPrefix}-${entry.id}`"
          :title="collapsed ? entry.label : undefined"
          @click="onHubClick(entry)"
        >
          <component
            :is="entry.icon"
            class="h-5 w-5 shrink-0"
            :style="accentStyle(entry)"
            aria-hidden="true"
          />
          <span :class="collapsed ? 'sr-only' : 'flex-1 truncate text-left'">{{ entry.label }}</span>
          <ChevronDownIcon
            v-if="!collapsed"
            class="h-4 w-4 shrink-0 transition-transform motion-reduce:transition-none"
            :class="isExpanded(entry) ? 'rotate-180' : ''"
            aria-hidden="true"
          />
        </button>

        <!-- Accordéon : grid-template-rows 0fr ↔ 1fr (pas de reflow, cf MASTER §8) -->
        <div
          :id="`submenu-${idPrefix}-${entry.id}`"
          class="grid transition-[grid-template-rows] duration-200 ease-out motion-reduce:transition-none"
          :class="isExpanded(entry) ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'"
        >
          <div class="overflow-hidden">
            <ul
              role="list"
              class="my-1 ml-5 flex flex-col gap-0.5 border-l-[3px] pl-3"
              :style="submenuBorderStyle(entry.accent)"
            >
              <li v-for="child in entry.children" :key="child.id">
                <!-- Groupe titré (sous-sous-menu, non repliable) -->
                <template v-if="child.children">
                  <p class="px-2 pb-1 pt-2 text-xs font-semibold uppercase tracking-wide text-base-content/70">
                    {{ child.label }}
                  </p>
                  <ul role="list" class="flex flex-col gap-0.5">
                    <li v-for="leaf in child.children" :key="leaf.id">
                      <RouterLink
                        v-if="leaf.to"
                        :to="leaf.to"
                        :class="linkClasses(leaf)"
                        :style="activeLinkStyle(isActive(leaf.to), entry.accent)"
                        :aria-current="isActive(leaf.to) ? 'page' : undefined"
                      >
                        <span class="flex-1 truncate text-left">{{ leaf.label }}</span>
                      </RouterLink>
                      <a
                        v-else-if="leaf.href"
                        :href="leaf.href"
                        target="_blank"
                        rel="noopener noreferrer"
                        :class="linkClasses(leaf)"
                      >
                        <span class="flex-1 truncate text-left">{{ leaf.label }}</span>
                        <ArrowTopRightOnSquareIcon class="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                      </a>
                    </li>
                  </ul>
                </template>

                <!-- Lien simple (niveau 2) -->
                <RouterLink
                  v-else-if="child.to"
                  :to="child.to"
                  :class="linkClasses(child)"
                  :style="activeLinkStyle(isActive(child.to), entry.accent)"
                  :aria-current="isActive(child.to) ? 'page' : undefined"
                >
                  <span class="flex-1 truncate text-left">{{ child.label }}</span>
                </RouterLink>
                <a
                  v-else-if="child.href"
                  :href="child.href"
                  target="_blank"
                  rel="noopener noreferrer"
                  :class="linkClasses(child)"
                >
                  <span class="flex-1 truncate text-left">{{ child.label }}</span>
                  <ArrowTopRightOnSquareIcon class="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                </a>
              </li>
            </ul>
          </div>
        </div>
      </template>

      <!-- Lien direct (niveau 1 sans enfants) -->
      <RouterLink
        v-else-if="entry.to"
        :to="entry.to"
        :class="[linkClasses(entry), collapsed ? 'justify-center px-0' : '']"
        :style="activeLinkStyle(isActive(entry.to), entry.accent)"
        :aria-current="isActive(entry.to) ? 'page' : undefined"
        :title="collapsed ? entry.label : undefined"
      >
        <component
          :is="entry.icon"
          class="h-5 w-5 shrink-0"
          :style="accentStyle(entry)"
          aria-hidden="true"
        />
        <span :class="collapsed ? 'sr-only' : 'flex-1 truncate text-left'">{{ entry.label }}</span>
      </RouterLink>
      <a
        v-else-if="entry.href"
        :href="entry.href"
        target="_blank"
        rel="noopener noreferrer"
        :class="[linkClasses(entry), collapsed ? 'justify-center px-0' : '']"
        :title="collapsed ? entry.label : undefined"
      >
        <component
          :is="entry.icon"
          class="h-5 w-5 shrink-0"
          :style="accentStyle(entry)"
          aria-hidden="true"
        />
        <span :class="collapsed ? 'sr-only' : 'flex-1 truncate text-left'">{{ entry.label }}</span>
        <ArrowTopRightOnSquareIcon v-if="!collapsed" class="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
      </a>
    </li>
  </ul>
</template>
